/**
 * Search box + decision filter pill tabs (Wszystkie / Zaakceptowane / Odrzucone
 * / Oczekujące, each with a live count) + the "N of M nośników" shown label.
 * Mirrors apps/mockups/.../toolbar.mockup.tsx, adapted to real i18n strings.
 */
import { useTranslation } from 'react-i18next';
import { ExpandingSearchInput } from '@ooh/ui/components/expanding-search-input';
import { cn } from '@ooh/ui/lib/utils';
import { Button } from '@ooh/ui/components/button';
import type { DecisionFilterKey } from './filters';
import { ColumnPicker, type ColumnPickerColumn } from '@ooh/ui/components/column-picker';
import { UNIT_DECISIONS_PINNED_COLUMN_IDS } from './table-columns';

interface FilterTabDef {
  key: DecisionFilterKey;
  label: string;
  count: number;
}

export interface UnitDecisionsToolbarProps {
  query: string;
  onQueryChange: (value: string) => void;
  activeFilter: DecisionFilterKey;
  onFilterChange: (key: DecisionFilterKey) => void;
  counts: { all: number; accepted: number; rejected: number; undecided: number };
  shownCount: number;
  columnPickerColumns: ColumnPickerColumn[];
  hiddenColumnIds: ReadonlySet<string>;
  onToggleColumn: (id: string) => void;
  onMoveColumnUp: (id: string) => void;
  onMoveColumnDown: (id: string) => void;
}

export function UnitDecisionsToolbar({
  query,
  onQueryChange,
  activeFilter,
  onFilterChange,
  counts,
  shownCount,
  columnPickerColumns,
  hiddenColumnIds,
  onToggleColumn,
  onMoveColumnUp,
  onMoveColumnDown,
}: UnitDecisionsToolbarProps) {
  const { t } = useTranslation('offers');

  const tabs: FilterTabDef[] = [
    {
      key: 'ALL',
      label: t('workspace.versions.unitDecisions.toolbar.filterAll'),
      count: counts.all,
    },
    {
      key: 'ACCEPTED',
      label: t('workspace.versions.unitDecisions.toolbar.filterAccepted'),
      count: counts.accepted,
    },
    {
      key: 'REJECTED',
      label: t('workspace.versions.unitDecisions.toolbar.filterRejected'),
      count: counts.rejected,
    },
    {
      key: 'UNDECIDED',
      label: t('workspace.versions.unitDecisions.toolbar.filterUndecided'),
      count: counts.undecided,
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="unit-decisions-toolbar">
      <ExpandingSearchInput
        // eslint-disable-next-line i18next/no-literal-string -- Tailwind utility classes, not user-facing
        collapsedWidthClassName="w-60"
        // eslint-disable-next-line i18next/no-literal-string -- Tailwind utility classes, not user-facing
        expandedWidthClassName="w-60"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder={t('workspace.versions.unitDecisions.toolbar.searchPlaceholder')}
        aria-label={t('workspace.versions.unitDecisions.toolbar.searchAriaLabel')}
        data-testid="unit-decisions-search-input"
      />

      <div className="flex gap-1.5">
        {tabs.map((tab) => {
          const active = tab.key === activeFilter;
          return (
            <Button
              key={tab.key}
              type="button"
              variant={active ? 'default' : 'outline'}
              size="sm"
              onClick={() => onFilterChange(tab.key)}
              data-testid={`unit-decisions-filter-${tab.key}`}
              className="gap-1.5 rounded-full"
            >
              {tab.label}
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums',
                  active ? 'bg-white/20 text-background' : 'bg-muted text-muted-foreground',
                )}
              >
                {tab.count}
              </span>
            </Button>
          );
        })}
      </div>

      <span
        className="ml-auto text-xs text-muted-foreground"
        data-testid="unit-decisions-shown-label"
      >
        {t('workspace.versions.unitDecisions.toolbar.shownLabel', {
          shown: shownCount,
          total: counts.all,
        })}
      </span>
      <ColumnPicker
        columns={columnPickerColumns}
        pinnedIds={UNIT_DECISIONS_PINNED_COLUMN_IDS}
        hiddenColumnIds={hiddenColumnIds}
        onToggle={onToggleColumn}
        onMoveUp={onMoveColumnUp}
        onMoveDown={onMoveColumnDown}
        label={t('workspace.unitsTab.columnVisibility.toggleButton')}
        sectionLabel={t('workspace.unitsTab.columnVisibility.sectionLabel')}
        searchPlaceholder={t('workspace.unitsTab.columnVisibility.searchPlaceholder')}
        noResults={t('workspace.unitsTab.columnVisibility.noResults')}
      />
    </div>
  );
}
